import {useMediaQuery} from '@material-ui/core';
import {Async, AsyncPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {constTrue} from 'fp-ts/lib/function';
import React, {ReactNode, useEffect} from 'react';
import Card from 'Shared/components/PagedView/Card/Card';
import {ColumnProps} from 'Shared/components/PagedView/Column';
import {FilterProperties} from 'Shared/components/PagedView/Filter';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import Table, {ReactWindowProps} from 'Shared/components/PagedView/Table/Table';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';

export type PagedViewProps<TData> = {
  data: AsyncPage<Page<TData>> | Async<Page<TData>>;
  eventAction?: string;
  loadMore: (page: Page<TData>) => void;
  actions?: JSX.Element;
  onChangeSort?: (sort?: Sort) => void;
  sort?: Sort;
  children: (virtualizedProps: ReactWindowProps<TData>) => JSX.Element;
  additionalFilterProperties?: AdditionalFilterProperty[];
  pagedViewKey: string;
  titleOrType?: string;
};

export type SharedPagedProps<TData> = {
  data: AsyncPage<Page<TData>> | Async<Page<TData>>;
  eventAction?: string;
  loadMore: (page: Page<TData>) => void;
  columns: Array<Column<TData>>;
  onChangeSort?: (sort?: Sort) => void;
  additionalFilterProperties?: AdditionalFilterProperty[];
  sort?: Sort;
  actions?: JSX.Element;
  pagedViewKey: string;
  titleOrType?: string;
};

const BlankVirtualizedPropsValues = {
  index: 0,
  dataItem: {} as any,
};

const toColumn = <TData extends {}>(columnElement: React.ReactElement<ColumnProps<TData, any>>): Column<TData> => {
  const {
    title,
    children,
    sortColumnKey,
    filterProperties,
    visible,
    isActions,
    default: isDefault,
    ddprivacy,
  } = columnElement.props;

  return {
    allowHide: !isActions,
    filter: filterProperties,
    getContent: children,
    sortColumnKey,
    title,
    visible: visible || constTrue,
    hiddenByDefault: isDefault === undefined || !isDefault,
    ddprivacy,
  };
};

export type AdditionalFilterProperty = {
  filter: FilterProperties<any>;
  title: string;
};

export type Column<TData> = {
  title: string;
  getContent: (data: TData) => ReactNode;
  sortColumnKey?: keyof TData;
  filter?: FilterProperties<any>;
  visible: () => boolean;
  allowHide?: boolean;
  hiddenByDefault: boolean;
  ddprivacy?: string;
};

const PagedView = <TData extends {}>(props: PagedViewProps<TData>) => {
  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);
  const dispatch = useDispatch();

  useEffect(() => {
    let updateFeature = {...featureVariables, FOOTER_OPEN_DEFAULT: false};
    dispatch(updatefeatureVariables(updateFeature));
  }, []);

  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));

  const columns: Array<Column<TData>> = props.children(BlankVirtualizedPropsValues).props.children.map(toColumn);
  const visibleColumns = columns.filter(col => col.visible());
  // @ts-ignore
  return isDesktop ? <Table {...props} columns={visibleColumns} /> : <Card {...props} columns={visibleColumns} />;
};

export default PagedView;
